// Light / dark / system, as a three-way control in the top bar.
//
// A radiogroup rather than a cycling button: a cycling button cannot tell you
// what the other options are, and "system" in particular is invisible unless
// it is named. Three small targets side by side also mean one press to reach
// any state instead of up to two.

import { Icon } from '@ng-unifi/ui';
import { useTheme, type ThemePreference } from '../lib/theme.js';

const OPTIONS: Array<{ value: ThemePreference; label: string; icon: string }> = [
  { value: 'light', label: 'Light', icon: 'light_mode' },
  { value: 'dark', label: 'Dark', icon: 'dark_mode' },
  { value: 'system', label: 'System', icon: 'contrast' },
];

export function ThemeToggle() {
  const { preference, resolved, setPreference } = useTheme();

  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      className="inline-flex items-center gap-0.5 rounded-full border border-outline-variant bg-surface-container p-0.5"
    >
      {OPTIONS.map((option) => {
        const selected = preference === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            // The label alone would read as "System" without saying what that
            // currently resolves to, which is the one thing a person checking
            // this control wants to know.
            aria-label={
              option.value === 'system'
                ? `System theme (currently ${resolved})`
                : `${option.label} theme`
            }
            onClick={() => setPreference(option.value)}
            // 32px is below the 24px AA floor by a comfortable margin and sits
            // in a low-frequency corner of the top bar; the row as a whole is
            // 44px tall for touch.
            className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              selected
                ? 'bg-surface-container-highest text-on-surface'
                : 'text-on-surface-variant hover:bg-surface-container-high'
            }`}
          >
            <Icon name={option.icon} className="text-[1.125rem]" aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}
